/**
 * Design Memory — the engine.
 *
 * Ties the fingerprint, similarity strategy, store, and constraint derivation
 * into one deterministic API. The similarity strategy and the store are both
 * injectable, so semantic/visual embeddings (a new strategy) and pgvector (a
 * new store) plug in without changing the public methods.
 *
 * The Thinking Engine consumes only `check().constraints.generationConstraints`;
 * it never sees the store or prior fingerprints.
 */

import { createFingerprint } from './fingerprint';
import { computeSimilarity } from './similarity';
import { computeSimilarityReport } from './similarityReport';
import { deriveConstraints } from './constraints';
import { InMemoryMemoryStore } from './memoryStore';
import { WEIGHTS, DEFAULT_THRESHOLDS, FINGERPRINT_SCHEMA_ID } from './memoryVocabulary';

const isFingerprint = (x) => x && typeof x === 'object' && x.schema === FINGERPRINT_SCHEMA_ID;

export class DesignMemory {
  /**
   * @param {MemoryStore} [store]     defaults to an in-memory store.
   * @param {Object} [config]
   * @param {Object} [config.weights]
   * @param {Object} [config.thresholds]
   * @param {Function} [config.similarity]  strategy — the embedding seam.
   */
  constructor(store = new InMemoryMemoryStore(), { weights = WEIGHTS, thresholds = DEFAULT_THRESHOLDS, similarity = computeSimilarity } = {}) {
    this.store = store;
    this.weights = weights;
    this.thresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
    this.similarity = similarity;
  }

  /** Descriptor → fingerprint (or pass a fingerprint through unchanged). */
  fingerprint(input) {
    return isFingerprint(input) ? input : createFingerprint(input);
  }

  /**
   * Evaluate a design against memory WITHOUT recording it.
   * @returns {{ fingerprint, report, constraints }}
   */
  check(input, { scope, limit } = {}) {
    const fingerprint = this.fingerprint(input);
    const stored = this.store.query({ scope, limit });
    const report = computeSimilarityReport(fingerprint, stored, {
      threshold: this.thresholds.duplicate,
      weights: this.weights,
      similarity: this.similarity,
    });
    const constraints = deriveConstraints(report, this.thresholds);
    return { fingerprint, report, constraints };
  }

  /** Record a design in memory. Returns the stored fingerprint. */
  remember(input, { scope } = {}) {
    const fingerprint = this.fingerprint(input);
    this.store.add(fingerprint, { scope });
    return fingerprint;
  }

  /** Check then remember — the common per-generation flow. */
  register(input, { scope, limit } = {}) {
    const result = this.check(input, { scope, limit });
    this.remember(result.fingerprint, { scope });
    return result;
  }

  size(options = {}) { return this.store.size(options); }
  clear(options = {}) { this.store.clear(options); }
}
